// BgpTicker v0.1.0 — inline ticker rendered inside IncidentBanner showing the
// most recent tier-1 BGP events from the bgp-worker. Cap at 8 entries so the
// banner stays a single horizontal flow on common viewports; older events are
// dropped (they're still in the KV ring buffer if anyone fetches /bgp/recent
// directly). Returns null on empty input so the parent can branch on length
// and skip the wrapper entirely.
import type { BgpEvent } from '../lib/api.js';

interface Props {
  events: BgpEvent[];
}

function fmtAge(ts: number): string {
  // Coarse "Ns ago" / "Nm ago" formatter — events are seconds-fresh during
  // active incidents; we don't need sub-second precision in a status ticker.
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s ago`;
  return `${Math.floor(sec / 60)}m ago`;
}

export default function BgpTicker({ events }: Props) {
  if (events.length === 0) return null;
  return (
    <div className="px-4 pb-2 max-w-[1400px] mx-auto font-mono text-[11px] text-cyan flex flex-wrap gap-x-4 gap-y-1">
      <span className="text-dim text-[10px] tracking-[2px] uppercase">BGP</span>
      {events.slice(0, 8).map((e) => (
        <span key={`${e.ts}-${e.prefix ?? ''}-${e.asn ?? ''}`}>
          AS{e.asn ?? '?'} <span className="text-magenta">{e.type}</span>{' '}
          <span className="text-text">{e.prefix ?? ''}</span>{' '}
          <span className="text-dim">· {fmtAge(e.ts)}</span>
        </span>
      ))}
    </div>
  );
}
